import { useVirtualizer } from '@tanstack/react-virtual';
import { first, isNil, last, sum } from 'lodash';
import { ReactNode, UIEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { CollectionViewAnalytics } from '@shared/scenes/cards/collectionView/CollectionViewAnalytics';
import { CollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/CollectionViewField';
import { getTableCellVariant } from '@shared/scenes/cards/tableView/getTableCellVariant';
import {
  getComponentLabel,
  isInputButtonComponent,
  isNumberComponent,
  isTimeTrackerComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { MIN_COLUMN_WIDTH, SubtableColumnMeasure } from './SubtableColumnMeasure';
import { getSubtableCellField, getSubtableRowNumber } from './subtableColumns';
import { isDraftRow } from './subtableDrafts';
import { getRowPressProps } from './subtableRowPress';
import { useRatchetedRowMeasure } from './useRatchetedRowMeasure';
import { SubtableRowRefs } from './useSubtableRows';
import styles from './SubtableTable.module.css';

const ESTIMATED_ROW_HEIGHT = 48;
/** The sticky header row's height (4.25Q in SubtableTable.module.css); rows scroll beneath it. */
const HEADER_HEIGHT = 34;
const ROW_OVERSCAN = 8;
const COLUMN_OVERSCAN = 2;
/** Start fetching the next page when the last rendered row is this close to the end. */
const LOAD_MORE_THRESHOLD = 5;
/** The row-number column ahead of the first column; pinned with it. */
const ROW_NUMBER_WIDTH = 40;

interface Props {
  workflowTemplateId: string;
  columns: API.AnyComponent[];
  rows: API.WorkflowEntityFragment[];
  refs?: SubtableRowRefs;
  hasMore: boolean;
  onLoadMore: () => void;
  /** Set by the SubtableTableModal to modify the table's styling for the modal. */
  modal?: boolean;
  /** The row open in the row menu, highlighted. */
  selectedRowId?: string;
  onRowPress: (entityId: string, index: number) => void;
  /**
   * Hides the controls that edit a row from its cell: input buttons, and a timer's start/stop
   * button. Other cells are display-only either way.
   */
  isReadOnly?: boolean;
}

/**
 * Desktop: the subtable's rows as a table. Columns are content-sized (see
 * SubtableColumnMeasure) and rows and columns are both virtualized inside the field's own
 * scroller, which is capped at about ten rows tall, with the header row pinned, and the row numbers
 * and first column pinned together.
 */
export function SubtableTable({
  workflowTemplateId,
  columns,
  rows,
  refs,
  hasMore,
  onLoadMore,
  modal = false,
  selectedRowId,
  onRowPress,
  isReadOnly = false,
}: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  // Drives the pinned column's edge shadow (data-scrolled in the CSS). Set only when it flips.
  const [isScrolledRight, setIsScrolledRight] = useState(false);
  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    setIsScrolledRight(event.currentTarget.scrollLeft > 0);
  };

  // CollectionViewField only reports analytics for Input Button columns.
  const analytics = useMemo(
    () => new CollectionViewAnalytics(workflowTemplateId, ''),
    [workflowTemplateId],
  );

  // The column virtualizer needs every width before rendering, and with rows virtualized most cells
  // aren't in the table's DOM to measure, so SubtableColumnMeasure renders every loaded cell once
  // in a hidden layer whenever the rows or columns change. Sizing from the table's rendered cells
  // would widen columns (and shift everything right of them) as rows scroll in.
  const [measured, setMeasured] = useState<{
    columns: API.AnyComponent[];
    rows: API.WorkflowEntityFragment[];
    refs: SubtableRowRefs | undefined;
    widths: number[];
  }>();
  const isMeasured =
    measured?.columns === columns && measured.rows === rows && measured.refs === refs;
  // Until the first measurement, which lands before the first paint.
  const widths = useMemo(
    () => measured?.widths ?? columns.map(() => MIN_COLUMN_WIDTH),
    [measured, columns],
  );
  const [pinnedColumn, ...scrollColumns] = columns;
  const [pinnedWidth = 0, ...scrollWidths] = widths;
  // The row numbers and the first column, pinned together ahead of the virtualized columns.
  const leadingWidth = ROW_NUMBER_WIDTH + pinnedWidth;
  const tableWidth = leadingWidth + sum(scrollWidths);

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: scrollColumns.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: index => scrollWidths[index] ?? 0,
    // The pinned columns sit at the start of the scroller, ahead of the virtualized columns.
    paddingStart: leadingWidth,
    overscan: COLUMN_OVERSCAN,
  });
  // Before paint, so the table never shows the widths from before a measurement.
  useLayoutEffect(() => {
    columnVirtualizer.measure();
  }, [columnVirtualizer, widths]);

  // Row heights only grow: a tall cell scrolled out of view horizontally mustn't shrink its row.
  // New widths mean edited data, which can make rows genuinely shorter, so they reset the heights.
  const measureRow = useRatchetedRowMeasure(widths);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    measureElement: measureRow,
    overscan: ROW_OVERSCAN,
    scrollMargin: HEADER_HEIGHT,
  });

  const virtualColumns = columnVirtualizer.getVirtualItems();
  const columnPaddingLeft = Math.max(0, (first(virtualColumns)?.start ?? 0) - leadingWidth);
  const columnPaddingRight = Math.max(
    0,
    columnVirtualizer.getTotalSize() - (last(virtualColumns)?.end ?? leadingWidth),
  );

  const virtualRows = rowVirtualizer.getVirtualItems();
  const lastRenderedIndex = last(virtualRows)?.index ?? -1;
  useEffect(() => {
    if (hasMore && lastRenderedIndex >= rows.length - LOAD_MORE_THRESHOLD) {
      onLoadMore();
    }
  }, [hasMore, lastRenderedIndex, rows.length, onLoadMore]);

  if (isNil(pinnedColumn)) {
    return null;
  }

  // Shared by the table and SubtableColumnMeasure, so columns are measured from what they render.
  const renderHeaderLabel = (column: API.AnyComponent) => (
    <Text
      size="overline"
      weight="semibold"
      inactive
      numberOfLines={1}
      className={styles.headerLabel}
    >
      {getComponentLabel(column)}
    </Text>
  );
  const renderField = (row: API.WorkflowEntityFragment, column: API.AnyComponent) => (
    <CollectionViewField
      field={getSubtableCellField(row, column, refs)}
      component={column}
      variant={getTableCellVariant(column)}
      showTimerIcon
      workflowTemplateId={workflowTemplateId}
      entityId={row.id}
      // Only controls act from a cell: an input button, and a timer's start/stop button.
      canUpdateCard={
        !isReadOnly &&
        !isDraftRow(row) &&
        (isInputButtonComponent(column) || isTimeTrackerComponent(column))
      }
      analytics={analytics}
    />
  );

  // Columns are virtualized, so each cell says which column it is (aria-colindex, 1-based: the
  // row number is 1, the pinned column 2); the virtualizer's spacers are hidden from assistive tech.
  const renderCells = (
    cellRole: 'columnheader' | 'cell',
    rowNumber: ReactNode,
    renderCell: (column: API.AnyComponent) => JSX.Element,
  ) => (
    <>
      <View
        role={cellRole}
        aria-colindex={1}
        className={[styles.cell, styles.rowNumber]}
        style={{ width: ROW_NUMBER_WIDTH }}
      >
        {rowNumber}
      </View>
      <View
        role={cellRole}
        aria-colindex={2}
        className={[styles.cell, styles.pinned]}
        style={{ width: pinnedWidth, left: ROW_NUMBER_WIDTH }}
      >
        {renderCell(pinnedColumn)}
      </View>
      {columnPaddingLeft > 0 && <View aria-hidden style={{ width: columnPaddingLeft }} />}
      {virtualColumns.map(virtualColumn => {
        const column = scrollColumns[virtualColumn.index];
        return (
          <View
            key={column.id}
            role={cellRole}
            aria-colindex={virtualColumn.index + 3}
            className={[styles.cell, isNumberComponent(column) && styles.numeric]}
            style={{ width: scrollWidths[virtualColumn.index] }}
          >
            {renderCell(column)}
          </View>
        );
      })}
      {columnPaddingRight > 0 && <View aria-hidden style={{ width: columnPaddingRight }} />}
    </>
  );

  return (
    <View
      ref={scrollerRef}
      className={[styles.scroller, modal && styles.modal]}
      data-scrolled={isScrolledRight || undefined}
      onScroll={onScroll}
    >
      {!isMeasured && (
        <SubtableColumnMeasure
          columns={columns}
          rows={rows}
          renderHeader={column => (
            <View row className={styles.headerRow}>
              <View className={[styles.cell, styles.measuring]}>{renderHeaderLabel(column)}</View>
            </View>
          )}
          renderCell={(row, column) => (
            <View className={[styles.cell, styles.measuring]}>{renderField(row, column)}</View>
          )}
          onMeasure={measuredWidths => setMeasured({ columns, rows, refs, widths: measuredWidths })}
        />
      )}
      {/* Rows are virtualized too: the counts and each row's aria-rowindex (the header is row 1)
          let screen readers announce real positions, not just the rendered slice. */}
      <View
        role="table"
        aria-rowcount={rows.length + 1}
        aria-colcount={columns.length + 1}
        className={styles.table}
        style={{ width: tableWidth }}
      >
        <View row role="row" aria-rowindex={1} className={styles.headerRow}>
          {/* The row-number column has no header. */}
          {renderCells('columnheader', null, renderHeaderLabel)}
        </View>
        <View
          role="rowgroup"
          className={styles.body}
          style={{ height: rowVirtualizer.getTotalSize() }}
        >
          {virtualRows.map(virtualRow => {
            const row = rows[virtualRow.index];
            return (
              <View
                key={row.id}
                ref={rowVirtualizer.measureElement}
                data-index={virtualRow.index}
                data-row-id={row.id}
                row
                role="row"
                aria-rowindex={virtualRow.index + 2}
                {...getRowPressProps(() => onRowPress(row.id, virtualRow.index))}
                className={[styles.row, row.id === selectedRowId && styles.selected]}
                style={{ transform: `translateY(${virtualRow.start - HEADER_HEIGHT}px)` }}
              >
                {renderCells(
                  'cell',
                  <Text size="body2" inactive>
                    {getSubtableRowNumber(virtualRow.index)}
                  </Text>,
                  column => renderField(row, column),
                )}
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
}
